// @ts-check
/// <reference lib="dom" />
/// <reference lib="es2020" />

/**
 * @typedef {import("@hotwired/stimulus").Application} Application
 * @typedef {import("@hotwired/stimulus").Controller} Controller
 */

/**
 * @typedef {'inViewport' | 'nearViewport' | 'nextTick' | 'immediate'} LoadStrategy
 */

/**
 * @typedef {Object} ControllerModule
 * @property {new (...args: any[]) => Controller} Controller
 */

/** @typedef {Element & { getAttribute(name: string): string | null, querySelectorAll(selector: string): NodeListOf<Element>, hasAttribute(name: string): boolean }} ElementWithController */

/**
 * A class to dynamically load Stimulus controllers with different loading strategies.
 */
export class StimulusLoader {
  /** @type {Application} */
  #application;

  /** @type {MutationObserver} */
  #observer;

  /** @type {IntersectionObserver} */
  #inViewportObserver;

  /** @type {Map<string, IntersectionObserver>} */
  #nearViewportObservers;

  /** @type {Record<LoadStrategy, (element: ElementWithController) => void>} */
  #strategyHandlers;

  /** @type {Set<string>} */
  #controllersCache;

  /**
   * Creates an instance of StimulusLoader.
   * @param {Application} application - The Stimulus application instance.
   */
  constructor(application) {
    this.#application = application;
    this.#observer = this.#createMutationObserver();
    this.#inViewportObserver = this.#createIntersectionObserver({});
    this.#nearViewportObservers = new Map();
    this.#strategyHandlers = {
      inViewport: (element) => this.#inViewportObserver.observe(element),
      nearViewport: (element) => this.#handleNearViewport(element),
      nextTick: (element) => this.#loadControllerOnNextIdle(element),
      immediate: (element) => this.#loadControllerForElement(element)
    };
    this.#controllersCache = new Set();
  }

  /**
   * Creates a MutationObserver to watch for DOM changes.
   * @returns {MutationObserver}
   */
  #createMutationObserver() {
    return new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            this.#handleNode(/** @type {Node} */ node);
          });
        }
      }
    });
  }

  /**
   * Handles a single node, applying the appropriate loading strategy.
   * @param {Node} node - The node to handle.
   */
  #handleNode(node) {
    if (node instanceof Element) {
      this.#handleElement(/** @type {ElementWithController} */ (node));
      if (node.hasChildNodes()) {
        node.childNodes.forEach((childNode) => {
          this.#handleNode(childNode);
        });
      }
    }
  }

  /**
   * Creates an IntersectionObserver with the given options.
   * @param {IntersectionObserverInit} options - The options for the IntersectionObserver.
   * @returns {IntersectionObserver}
   */
  #createIntersectionObserver(options) {
    return new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.#loadControllerForElement(
            /** @type {ElementWithController} */ (entry.target)
          );
          this.#inViewportObserver.unobserve(entry.target);
          this.#nearViewportObservers.forEach((observer) =>
            observer.unobserve(entry.target)
          );
        }
      });
    }, options);
  }

  /**
   * Handles the nearViewport strategy for an element.
   * @param {ElementWithController} element - The element to handle.
   */
  #handleNearViewport(element) {
    const rootMargin =
      element.getAttribute('data-stimulus-load-root-margin') || '600px';
    let observer = this.#nearViewportObservers.get(rootMargin);

    if (!observer) {
      observer = this.#createIntersectionObserver({ rootMargin });
      this.#nearViewportObservers.set(rootMargin, observer);
    }

    observer.observe(element);
  }

  /**
   * Handles a single element, applying the appropriate loading strategy.
   * @param {ElementWithController} element - The element to handle.
   */
  #handleElement(element) {
    const controllerName = element.getAttribute('data-controller');
    if (controllerName && controllerName.startsWith('elv--')) {
      const strategy = /** @type {LoadStrategy} */ (
        element.getAttribute('data-stimulus-load-strategy')
      );
      const handler =
        this.#strategyHandlers[strategy] || this.#strategyHandlers.nextTick;
      handler(element);
    }
  }

  /**
   * Loads the controller for a given element on the next idle callback.
   * @param {ElementWithController} element - The element to load the controller for.
   */
  #loadControllerOnNextIdle(element) {
    /** @type {IdleRequestCallback} */
    const callback = () => {
      this.#loadControllerForElement(element);
    };
    window.requestIdleCallback(callback);
  }

  /**
   * Loads the controller for a given element.
   * @param {ElementWithController} element - The element to load the controller for.
   * @returns {Promise<void>}
   */
  async #loadControllerForElement(element) {
    const controllerName = element.getAttribute('data-controller');
    const isRegistered =
      this.#application.router['modulesByIdentifier'].get(controllerName);
    const isHandled = this.#controllersCache.has(controllerName);
    if (controllerName && !isHandled && !isRegistered) {
      try {
        this.#controllersCache.add(controllerName);
        await this.#loadController(controllerName);
      } catch (error) {
        this.#controllersCache.delete(controllerName);
        console.error(`Failed to load controller for ${controllerName}`, error);
      }
    }
  }

  /**
   * Loads a controller module and registers it with Stimulus.
   * @param {string} controllerName - The name of the controller to load.
   * @returns {Promise<void>}
   */
  async #loadController(controllerName) {
    const modulePath = this.#getModulePath(controllerName);
    /** @type {ControllerModule} */
    const module = await import(`@components/${modulePath}/controller.js`);
    this.#application.register(controllerName, module.Controller);
  }

  /**
   * Converts a controller name to its corresponding module path.
   * @param {string} controllerName - The name of the controller.
   * @returns {string} The module path.
   */
  #getModulePath(controllerName) {
    return controllerName
      .replace('elv--', '')
      .replace('-controller', '')
      .split('--')
      .map((segment) => segment.replace(/-/g, '_'))
      .join('/');
  }

  /**
   * Starts observing the document for changes and handles existing elements.
   */
  observe() {
    // Handle existing elements
    document
      .querySelectorAll('[data-controller^="elv--"]')
      .forEach((element) => {
        this.#handleElement(/** @type {ElementWithController} */ (element));
      });

    // Observe the entire document for changes
    this.#observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Stops observing the document for changes.
   */
  disconnect() {
    this.#observer.disconnect();
    this.#inViewportObserver.disconnect();
    this.#nearViewportObservers.forEach((observer) => observer.disconnect());
  }
}
